import React, { useState } from 'react';
import { useSidebarOpenState } from '@backstage/core-components';
import {
  makeStyles,
  Tooltip,
  IconButton,
  Typography,
  Box,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { useSystemInfo } from '../../buildInfo';
import { BuildInfoModal } from './BuildInfoModal';

const useStyles = makeStyles(theme => ({
  rootExpanded: {
    padding: theme.spacing(1, 2),
    margin: theme.spacing(0.5, 1, 1, 1),
    borderRadius: 6,
    cursor: 'pointer',
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.04)'
        : 'rgba(0, 0, 0, 0.03)',
    border: `1px solid ${
      theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.06)'
    }`,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
    '&:hover': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255, 255, 255, 0.08)'
          : 'rgba(0, 0, 0, 0.06)',
      borderColor: theme.palette.primary.main,
    },
  },
  rootCollapsed: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1, 0),
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: '#10b981',
    boxShadow: '0 0 6px #10b981',
    flexShrink: 0,
    marginTop: 4,
    marginRight: theme.spacing(1),
  },
  tagText: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 140,
  },
  subText: {
    fontSize: '0.65rem',
    lineHeight: 1.3,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginTop: 2,
  },
  infoIcon: {
    opacity: 0.6,
    fontSize: 15,
    marginTop: 2,
    marginLeft: theme.spacing(0.5),
    flexShrink: 0,
  },
  iconButton: {
    padding: 6,
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
}));

export const SidebarBuildInfo: React.FC = () => {
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();
  const { data } = useSystemInfo();
  const [modalOpen, setModalOpen] = useState(false);

  const build = data?.build;
  const k8s = data?.kubernetes;
  const displayTag = build?.imageTag || build?.commitShortSha || 'local-dev';
  const displayNode = k8s?.nodeName
    ? k8s.nodeName.replace(/^indectron-/, '')
    : 'local';

  const tooltipText = `${displayTag} • ${k8s?.nodeName || 'local'} (${
    k8s?.clusterName || 'cluster'
  })\nClick for build & instance info`;

  return (
    <>
      {isOpen ? (
        <Tooltip title={tooltipText} arrow placement="right">
          <div
            className={classes.rootExpanded}
            onClick={() => setModalOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && setModalOpen(true)}
          >
            <Box display="flex" alignItems="flex-start" overflow="hidden">
              <span className={classes.statusDot} />
              <Box display="flex" flexDirection="column" overflow="hidden">
                <Typography className={classes.tagText}>
                  {displayTag}
                </Typography>
                <Typography className={classes.subText}>
                  {displayNode} • {k8s?.clusterName || 'indectron'}
                </Typography>
              </Box>
            </Box>
            <InfoOutlinedIcon className={classes.infoIcon} />
          </div>
        </Tooltip>
      ) : (
        <div className={classes.rootCollapsed}>
          <Tooltip title={tooltipText} arrow placement="right">
            <IconButton
              size="small"
              className={classes.iconButton}
              onClick={() => setModalOpen(true)}
              aria-label="Instance build info"
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      )}

      <BuildInfoModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        systemInfo={data}
      />
    </>
  );
};
